import * as React from "react";
import { FormGroup } from "../RightAsideHelpers";
import { WorkflowNode, ClassificationRoute } from "../../types";
import { TeamSelect } from "@/components/messaging/TeamSelect";
import IconPlus from "@/icons/IconPlus";
import IconClose from "@/icons/IconClose";
import TextArea from "@/components/TextArea";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

export default function IntentHandoffEditor({ node, onUpdate }: Props) {
  const routes: ClassificationRoute[] = Array.isArray(node.intentRoutes)
    ? node.intentRoutes
    : [];

  function updateRoute(index: number, patch: Partial<ClassificationRoute>) {
    const updated = routes.map((r, i) => (i === index ? { ...r, ...patch } : r));
    onUpdate({ intentRoutes: updated });
  }

  function addRoute() {
    onUpdate({ intentRoutes: [...routes, { label: "", teamId: 0 }] });
  }

  function removeRoute(index: number) {
    onUpdate({ intentRoutes: routes.filter((_, i) => i !== index) });
  }

  return (
    <>
      <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800 leading-relaxed">
        <span className="font-semibold">Post-bot handoff: </span>
        This node runs after the AI conversation ends. It reads the recent transcript,
        detects the topic, and routes to the matching team — no follow-up questions asked.
        Unmatched topics and offline teams fall through to your routing rules.
      </div>

      <FormGroup
        label="Classification prompt"
        tooltipText="Describe the topics you want to detect. The AI reads the recent conversation and returns one of your labels."
      >
        <TextArea
          value={node.classificationPrompt ?? ""}
          onChange={(e) => onUpdate({ classificationPrompt: e.target.value })}
          placeholder={`Example:\nBased on the conversation, classify the visitor's topic into exactly one label:\n- billing: questions about invoices, payments, or subscriptions\n- returns: return, refund, or exchange requests\n- technical: bugs, errors, or integration problems\n- general: anything else`}
          className="w-full min-h-[120px]"
        />
        <p className="text-xs text-gray-400 mt-1">
          The AI classifies based on the last ~15 messages. Keep labels lowercase and unique.
        </p>
      </FormGroup>

      <FormGroup label="Intent routes">
        {routes.length === 0 && (
          <p className="text-xs text-gray-400 mb-2">
            Add at least one label → team mapping.
          </p>
        )}
        <div className="flex flex-col gap-2">
          {routes.map((route, i) => (
            <div key={i} className="flex gap-2 items-start">
              <input
                type="text"
                className="input flex-1"
                placeholder="label (e.g. billing)"
                value={route.label}
                onChange={(e) => updateRoute(i, { label: e.target.value })}
              />
              <div className="flex-1">
                <TeamSelect
                  value={route.teamId || undefined}
                  onChange={(teamId) => updateRoute(i, { teamId: teamId ?? undefined })}
                />
              </div>
              <button
                type="button"
                aria-label="Remove route"
                className="mt-1 text-gray-400 hover:text-red-500"
                onClick={() => removeRoute(i)}
              >
                <IconClose />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="mt-2 flex items-center gap-1 text-sm text-blue-500 hover:text-blue-600"
          onClick={addRoute}
        >
          <IconPlus />
          Add route
        </button>
      </FormGroup>

      <FormGroup
        label="Fallback team"
        tooltipText="Used when the conversation topic doesn't match any label, or when the AI call fails. Null = stay on the current team and let routing rules handle it."
      >
        <TeamSelect
          value={node.intentFallbackTeamId ?? undefined}
          onChange={(teamId) =>
            onUpdate({ intentFallbackTeamId: teamId || undefined })
          }
        />
        <div className="mt-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600 leading-relaxed">
          <div className="font-semibold text-slate-700 mb-0.5">Fallback fires when:</div>
          <ul className="list-disc list-inside space-y-0.5">
            <li>Conversation topic didn't match any of your labels</li>
            <li>AI service was unavailable or timed out</li>
            <li>No routes configured yet</li>
          </ul>
          <div className="mt-1.5">
            {node.intentFallbackTeamId
              ? "The visitor routes to the selected fallback team."
              : <span className="text-slate-500">No fallback selected — routing rules for the current team handle it (team bot, queue, or round-robin).</span>
            }
          </div>
        </div>
      </FormGroup>
    </>
  );
}
